import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { css } from '@styled-system/css'

import { Box, Flex, P } from 'ui/bend'
import { Input } from 'ui/bend/blocks/FormElements'

const Section = styled(Box)(
  css({
    mb: '24px',
    pb: '24px',
    borderBottom: '1px solid',
    borderColor: 'grey2'
  })
)

const SectionTitle = styled(P)(
  css({
    m: 0,
    mb: '16px',
    color: 'primary',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '150%'
  })
)

const Field = styled(Box)(
  css({
    mb: '16px'
  })
)

const ReadOnlyValue = styled(P)(
  css({
    m: 0,
    py: '13px',
    px: '16px',
    border: '1px solid',
    borderColor: 'grey2',
    backgroundColor: 'grey1',
    color: 'primary',
    fontSize: '15px',
    lineHeight: '150%'
  })
)

const Helper = styled(P)(
  css({
    m: 0,
    mt: '8px',
    color: 'grey4',
    fontSize: '13px',
    lineHeight: '150%'
  })
)

const DefaultGuestFields = ({
  user,
  registrationDefaults = {},
  nameFields = {},
  nameErrors = {},
  phoneNumber,
  phoneError,
  onNameChange,
  onPhoneChange
}) => {
  if (!user) return null

  const nameRequirement = registrationDefaults.nameRequirement || 'full-name'
  const phoneRequirement = registrationDefaults.phoneNumberRequirement
  const showPhone =
    phoneRequirement === 'required' || phoneRequirement === 'optional'

  return (
    <Section>
      <SectionTitle>Your information</SectionTitle>
      <Helper mb='16px'>
        Email comes from your ALO account. You can edit your name for this
        event.
      </Helper>

      {nameRequirement === 'first-last' ? (
        <Flex flexDirection={['column', 'row']} gap='12px'>
          <Field flex='1'>
            <Input
              id='first_name'
              label='First name *'
              value={nameFields.firstName || ''}
              onChange={(event) =>
                onNameChange?.('firstName', event.target.value)
              }
            />
            {nameErrors.firstName && (
              <P color='errorDefault' fontSize='13px' mt='8px' mb={0}>
                {nameErrors.firstName}
              </P>
            )}
          </Field>
          <Field flex='1'>
            <Input
              id='last_name'
              label='Last name *'
              value={nameFields.lastName || ''}
              onChange={(event) =>
                onNameChange?.('lastName', event.target.value)
              }
            />
            {nameErrors.lastName && (
              <P color='errorDefault' fontSize='13px' mt='8px' mb={0}>
                {nameErrors.lastName}
              </P>
            )}
          </Field>
        </Flex>
      ) : (
        <Field>
          <Input
            id='full_name'
            label='Name *'
            value={nameFields.fullName || ''}
            onChange={(event) => onNameChange?.('fullName', event.target.value)}
          />
          {nameErrors.fullName && (
            <P color='errorDefault' fontSize='13px' mt='8px' mb={0}>
              {nameErrors.fullName}
            </P>
          )}
        </Field>
      )}

      <Field>
        <P color='grey4' fontSize='13px' mb='8px' mt={0}>
          Email
        </P>
        <ReadOnlyValue>{user.email || '—'}</ReadOnlyValue>
      </Field>

      {showPhone && (
        <Field>
          <Input
            id='phone_number'
            label={`Phone number${phoneRequirement === 'required' ? ' *' : ''}`}
            value={phoneNumber || ''}
            onChange={(event) => onPhoneChange?.(event.target.value)}
          />
          {phoneError && (
            <P color='errorDefault' fontSize='13px' mt='8px' mb={0}>
              {phoneError}
            </P>
          )}
        </Field>
      )}
    </Section>
  )
}

DefaultGuestFields.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string
  }),
  registrationDefaults: PropTypes.shape({
    nameRequirement: PropTypes.oneOf(['full-name', 'first-last']),
    phoneNumberRequirement: PropTypes.oneOf(['optional', 'required'])
  }),
  nameFields: PropTypes.shape({
    fullName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string
  }),
  nameErrors: PropTypes.shape({
    fullName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string
  }),
  phoneNumber: PropTypes.string,
  phoneError: PropTypes.string,
  onNameChange: PropTypes.func,
  onPhoneChange: PropTypes.func
}

export default DefaultGuestFields
